import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Select, { components } from 'react-select'
import { Plus, Pencil, Trash2, RefreshCw, X, Check, Sparkles } from 'lucide-react'
import styles from './ClientSpecialMoments.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost, formatOccasionMonthDay } from '../../Content/SpecialMoments/smApi'
import { prettifyOccasionType, getIconComponent } from '../../Content/SpecialMoments/smIcons'
import SMSetupState from '../../Content/SpecialMoments/SMSetupState'

const SM_GET_CUSTOMER_PROFILE = 'sm_get_customer_profile'
const SM_GET_STORE_OCCASIONS = 'sm_get_store_occasions'
const SM_ADD_CLIENT_OCCASION = 'sm_add_client_occasion'
const SM_UPDATE_CLIENT_OCCASION = 'sm_update_client_occasion'
const SM_REMOVE_CLIENT_OCCASION = 'sm_remove_client_occasion'

const emptyAddForm = { storeOccasionId: '', occasionDate: '', title: '', partnerName: '' }
const emptyEditForm = { occasionDate: '', title: '', partnerName: '' }

const pad2 = (n) => String(n).padStart(2, '0')

// sm_get_customer_profile only gives occasion_date as "MM-DD" — no year.
// But turning_age / years_together are derived server-side from the real
// stored year, so we can reconstruct it (currentYear - age) to prefill the
// edit form's date input, instead of leaving it blank as if unknown.
const deriveEditableDate = (occasion) => {
    const [month, day] = (occasion.occasion_date || '').split('-').map(Number)
    if (!month || !day) return ''

    const currentYear = new Date().getFullYear()
    let year = null
    if (occasion.turning_age != null) year = currentYear - occasion.turning_age
    else if (occasion.years_together != null) year = currentYear - occasion.years_together

    if (year == null) return ''
    return `${year}-${pad2(month)}-${pad2(day)}`
}

// Matches this tab's pink brand accent (#db2777) instead of react-select's default blue.
const occasionSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 37,
        borderRadius: 7,
        borderColor: state.isFocused ? '#db2777' : '#dfdfdf',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(219, 39, 119, 0.12)' : 'none',
        cursor: 'pointer',
        '&:hover': { borderColor: state.isFocused ? '#db2777' : '#c9ccd1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 10px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '0.82rem' }),
    placeholder: (base) => ({ ...base, color: '#9aa1ac', fontSize: '0.82rem' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#9aa1ac', padding: 6 }),
    menu: (base) => ({
        ...base,
        borderRadius: 10,
        border: '1px solid #eceef1',
        boxShadow: '0 12px 28px rgba(16, 24, 40, 0.12)',
        overflow: 'hidden',
        zIndex: 20,
    }),
    menuList: (base) => ({ ...base, padding: 5 }),
    option: (base, state) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: '0.82rem',
        borderRadius: 7,
        padding: '8px 10px',
        backgroundColor: state.isSelected ? '#db2777' : state.isFocused ? '#fdf1f6' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#1a1a1a',
        cursor: 'pointer',
    }),
    noOptionsMessage: (base) => ({ ...base, fontSize: '0.8rem', color: '#9aa1ac' }),
}

const OccasionOptionLabel = ({ icon, label }) => {
    const Icon = getIconComponent(icon)
    return (
        <span className={styles.selectOptionLabel}>
            <Icon size={14} />
            {label}
        </span>
    )
}

const OccasionSelectOption = (props) => (
    <components.Option {...props}>
        <OccasionOptionLabel icon={props.data.icon} label={props.data.label} />
    </components.Option>
)

const OccasionSelectSingleValue = (props) => (
    <components.SingleValue {...props}>
        <OccasionOptionLabel icon={props.data.icon} label={props.data.label} />
    </components.SingleValue>
)

const ClientSpecialMoments = ({ clientStoreRelationIdentifier }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [occasions, setOccasions] = useState([])
    const [storeOccasions, setStoreOccasions] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const [showAddForm, setShowAddForm] = useState(false)
    const [addForm, setAddForm] = useState(emptyAddForm)
    const [adding, setAdding] = useState(false)

    const [editingOccasionId, setEditingOccasionId] = useState(null)
    const [editForm, setEditForm] = useState(emptyEditForm)
    const [savingEdit, setSavingEdit] = useState(false)

    const [removingOccasionId, setRemovingOccasionId] = useState(null)
    const [pendingRemoval, setPendingRemoval] = useState(null)

    const loadAll = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)

        const [profileRes, storeOccasionsRes] = await Promise.all([
            smPost(authCtx, activeStore, SM_GET_CUSTOMER_PROFILE, {
                client_store_relation_identifier: clientStoreRelationIdentifier,
            }),
            smPost(authCtx, activeStore, SM_GET_STORE_OCCASIONS, { enabled_only: true }),
        ])

        if (profileRes?.status === 'success' && profileRes?.response) {
            setOccasions(profileRes.response.occasions || [])
        } else {
            setLoadErrorMessage(profileRes?.message || "Couldn't load this client's special moments right now.")
        }

        if (storeOccasionsRes?.status === 'success' && storeOccasionsRes?.response) {
            setStoreOccasions(storeOccasionsRes.response.occasions || [])
        }

        setLoading(false)
    }, [authCtx, activeStore, clientStoreRelationIdentifier])

    useEffect(() => {
        if (activeStore?.storeIdentifier && clientStoreRelationIdentifier) loadAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, clientStoreRelationIdentifier])

    const hasStoreOccasions = storeOccasions.length > 0

    const occasionOptions = useMemo(
        () => storeOccasions.map((o) => ({ value: o.store_occasion_id, label: o.label, icon: o.icon, occasion_key: o.occasion_key })),
        [storeOccasions]
    )
    const selectedOccasionOption = occasionOptions.find(
        (opt) => String(opt.value) === String(addForm.storeOccasionId)
    ) || null

    const selectedStoreOccasion = storeOccasions.find(
        (o) => String(o.store_occasion_id) === String(addForm.storeOccasionId)
    )
    const addNeedsPartnerName = selectedStoreOccasion?.occasion_key === 'anniversary'

    const handleAddSubmit = async () => {
        if (!addForm.storeOccasionId) {
            toast.error('Choose an occasion type first.')
            return
        }
        if (!addForm.occasionDate) {
            toast.error('Pick a date for this occasion.')
            return
        }

        setAdding(true)
        const res = await smPost(authCtx, activeStore, SM_ADD_CLIENT_OCCASION, {
            client_store_relation_identifier: clientStoreRelationIdentifier,
            store_occasion_id: Number(addForm.storeOccasionId),
            occasion_date: addForm.occasionDate,
            title: addForm.title.trim() || null,
            occasion_metadata: addNeedsPartnerName && addForm.partnerName.trim()
                ? { partnerName: addForm.partnerName.trim() }
                : null,
        })
        setAdding(false)

        if (res?.status === 'success' && res?.response) {
            toast.success('Special moment added')
            setAddForm(emptyAddForm)
            setShowAddForm(false)
            loadAll()
        } else {
            toast.error(res?.message || "Couldn't add this occasion right now.")
        }
    }

    const startEdit = (occasion) => {
        setEditingOccasionId(occasion.occasion_id)
        setEditForm({ ...emptyEditForm, occasionDate: deriveEditableDate(occasion) })
    }

    const handleEditSubmit = async (occasion) => {
        const payload = { occasion_id: occasion.occasion_id }
        if (editForm.occasionDate) payload.occasion_date = editForm.occasionDate
        if (editForm.title.trim()) payload.title = editForm.title.trim()
        if (occasion.occasion_type === 'anniversary' && editForm.partnerName.trim()) {
            payload.occasion_metadata = { partnerName: editForm.partnerName.trim() }
        }

        setSavingEdit(true)
        const res = await smPost(authCtx, activeStore, SM_UPDATE_CLIENT_OCCASION, payload)
        setSavingEdit(false)

        if (res?.status === 'success' && res?.response) {
            toast.success('Special moment updated')
            setEditingOccasionId(null)
            loadAll()
        } else {
            toast.error(res?.message || "Couldn't update this occasion right now.")
        }
    }

    const confirmRemoval = async () => {
        const occasion = pendingRemoval
        if (!occasion) return

        setRemovingOccasionId(occasion.occasion_id)
        const res = await smPost(authCtx, activeStore, SM_REMOVE_CLIENT_OCCASION, {
            occasion_id: occasion.occasion_id,
        })
        setRemovingOccasionId(null)
        setPendingRemoval(null)

        if (res?.status === 'success' && res?.response) {
            toast.success('Special moment removed')
            setOccasions((prev) => prev.filter((o) => o.occasion_id !== occasion.occasion_id))
        } else {
            toast.error(res?.message || "Couldn't remove this occasion right now.")
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={16} className={styles.spinner} />
                <span>Loading special moments…</span>
            </div>
        )
    }

    if (loadErrorMessage) {
        return (
            <div className={styles.loadingWrap}>
                <span>{loadErrorMessage}</span>
                <button type="button" className={styles.retryBtn} onClick={loadAll}>Retry</button>
            </div>
        )
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.headerTitleRow}>
                    <Sparkles size={16} className={styles.headerIcon} />
                    <span className={styles.headerTitle}>Special Moments</span>
                </div>
                <p className={styles.headerSubtitle}>
                    Birthdays and anniversaries this client will be wished for automatically.
                </p>
            </div>

            <div className={styles.list}>
                {occasions.length === 0 && !showAddForm && (
                    <div className={styles.emptyState}>No special moments on file for this client yet.</div>
                )}

                {occasions.map((o) => {
                    const Icon = getIconComponent(o.icon)
                    const isEditing = editingOccasionId === o.occasion_id
                    const detail = o.turning_age != null
                        ? `Turning ${o.turning_age}`
                        : o.years_together != null
                            ? `${o.years_together} Years Together`
                            : null

                    return (
                        <div key={o.occasion_id} className={styles.card}>
                            <div className={styles.cardTop}>
                                <div className={styles.cardIconWrap}>
                                    <Icon size={16} />
                                </div>
                                <div className={styles.cardText}>
                                    <div className={styles.cardTitle}>{prettifyOccasionType(o.occasion_type)}</div>
                                    <div className={styles.cardMeta}>
                                        {formatOccasionMonthDay(o.occasion_date)}
                                        {detail && <> • {detail}</>}
                                    </div>
                                </div>
                                {!isEditing && (
                                    <div className={styles.cardActions}>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => startEdit(o)}
                                            aria-label="Edit"
                                        >
                                            <Pencil size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                            onClick={() => setPendingRemoval(o)}
                                            disabled={removingOccasionId === o.occasion_id}
                                            aria-label="Remove"
                                        >
                                            {removingOccasionId === o.occasion_id
                                                ? <RefreshCw size={14} className={styles.spinner} />
                                                : <Trash2 size={14} />}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {isEditing && (
                                <div className={styles.editForm}>
                                    <div className={styles.formRow}>
                                        <label className={styles.formLabel}>Correct the date</label>
                                        <input
                                            type="date"
                                            className={styles.formInput}
                                            value={editForm.occasionDate}
                                            onChange={(e) => setEditForm((f) => ({ ...f, occasionDate: e.target.value }))}
                                        />
                                    </div>
                                    <div className={styles.formRow}>
                                        <label className={styles.formLabel}>Title (optional)</label>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Leave blank to keep unchanged"
                                            value={editForm.title}
                                            onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                                        />
                                    </div>
                                    {o.occasion_type === 'anniversary' && (
                                        <div className={styles.formRow}>
                                            <label className={styles.formLabel}>Partner's name (optional)</label>
                                            <input
                                                type="text"
                                                className={styles.formInput}
                                                placeholder="Leave blank to keep unchanged"
                                                value={editForm.partnerName}
                                                onChange={(e) => setEditForm((f) => ({ ...f, partnerName: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                    <div className={styles.formActions}>
                                        <button
                                            type="button"
                                            className={styles.cancelBtn}
                                            onClick={() => setEditingOccasionId(null)}
                                        >
                                            <X size={13} /> Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.saveBtn}
                                            onClick={() => handleEditSubmit(o)}
                                            disabled={savingEdit}
                                        >
                                            {savingEdit ? <RefreshCw size={13} className={styles.spinner} /> : <Check size={13} />}
                                            {' '}Save
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {!hasStoreOccasions ? (
                <SMSetupState
                    message="No occasion types are enabled for this store yet. Turn on birthdays, anniversaries, or add a custom occasion to start tracking special moments for your clients."
                    ctaLabel="Add Now"
                    ctaHref="/special-moments/settings"
                />
            ) : showAddForm ? (
                <div className={styles.addForm}>
                    <div className={styles.formRow}>
                        <label className={styles.formLabel}>Occasion type</label>
                        <Select
                            classNamePrefix="occasionSelect"
                            styles={occasionSelectStyles}
                            options={occasionOptions}
                            value={selectedOccasionOption}
                            onChange={(selected) => setAddForm((f) => ({ ...f, storeOccasionId: selected ? String(selected.value) : '' }))}
                            components={{ Option: OccasionSelectOption, SingleValue: OccasionSelectSingleValue }}
                            placeholder="Select an occasion type"
                            isClearable
                            isSearchable
                            noOptionsMessage={() => 'No occasion types available'}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <label className={styles.formLabel}>Date</label>
                        <input
                            type="date"
                            className={styles.formInput}
                            value={addForm.occasionDate}
                            onChange={(e) => setAddForm((f) => ({ ...f, occasionDate: e.target.value }))}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <label className={styles.formLabel}>Title (optional)</label>
                        <input
                            type="text"
                            className={styles.formInput}
                            placeholder="e.g. 30th Birthday"
                            value={addForm.title}
                            onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
                        />
                    </div>
                    {addNeedsPartnerName && (
                        <div className={styles.formRow}>
                            <label className={styles.formLabel}>Partner's name (optional)</label>
                            <input
                                type="text"
                                className={styles.formInput}
                                placeholder="e.g. Neha"
                                value={addForm.partnerName}
                                onChange={(e) => setAddForm((f) => ({ ...f, partnerName: e.target.value }))}
                            />
                        </div>
                    )}
                    <div className={styles.formActions}>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={() => { setShowAddForm(false); setAddForm(emptyAddForm) }}
                        >
                            <X size={13} /> Cancel
                        </button>
                        <button type="button" className={styles.saveBtn} onClick={handleAddSubmit} disabled={adding}>
                            {adding ? <RefreshCw size={13} className={styles.spinner} /> : <Plus size={13} />}
                            {' '}Add
                        </button>
                    </div>
                </div>
            ) : (
                <button type="button" className={styles.addTriggerBtn} onClick={() => setShowAddForm(true)}>
                    <Plus size={14} /> Add Special Moment
                </button>
            )}

            {pendingRemoval && (
                <div
                    className={styles.modalOverlay}
                    onClick={() => !removingOccasionId && setPendingRemoval(null)}
                >
                    <div
                        className={styles.modalCard}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="removeOccasionTitle"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalIconWrap}>
                            <Trash2 size={18} />
                        </div>
                        <div id="removeOccasionTitle" className={styles.modalTitle}>
                            Remove this special moment?
                        </div>
                        <p className={styles.modalMessage}>
                            {prettifyOccasionType(pendingRemoval.occasion_type)} for this client will be permanently
                            removed. This can't be undone.
                        </p>
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={() => setPendingRemoval(null)}
                                disabled={!!removingOccasionId}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.modalDangerBtn}
                                onClick={confirmRemoval}
                                disabled={!!removingOccasionId}
                            >
                                {removingOccasionId
                                    ? <RefreshCw size={13} className={styles.spinner} />
                                    : <Trash2 size={13} />}
                                {' '}Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ClientSpecialMoments
